import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { referralsAPI, vansAPI, authAPI } from '../lib/api';
import { User, Mail, Shield, Truck, Copy, Gift, Users, CheckCircle, Clock, AlertCircle, Loader2, Lock, Eye, EyeOff, KeyRound, FileText, Globe } from 'lucide-react';
import { toast } from 'sonner';
import MFASecurity from '../components/MFASecurity';

export const Profile = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [referralStats, setReferralStats] = useState(null);
  const [assignedVehicle, setAssignedVehicle] = useState(null);
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [referralRes] = await Promise.all([
        referralsAPI.getMyReferrals(),
      ]);
      setReferralStats(referralRes.data);
      
      // Fetch assigned vehicle name if user has one
      if (user?.assigned_van_id) {
        try {
          const vanRes = await vansAPI.get(user.assigned_van_id);
          setAssignedVehicle(vanRes.data);
        } catch (e) {
          // Vehicle might not exist or user doesn't have access
        }
      }
    } catch (error) {
      console.error('Failed to load profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (referralStats?.referral_code) {
      navigator.clipboard.writeText(referralStats.referral_code);
      toast.success('Referral code copied to clipboard!');
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      owner: { color: 'bg-purple-100 text-purple-700', label: 'Owner' },
      office: { color: 'bg-blue-100 text-blue-700', label: 'Office Staff' },
      tech: { color: 'bg-green-100 text-green-700', label: 'Technician' },
    };
    const config = styles[role] || styles.tech;
    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      invited: { icon: Mail, color: 'bg-gray-100 text-gray-600', label: 'Invited' },
      signed_up: { icon: Clock, color: 'bg-blue-100 text-blue-600', label: 'Started Trial' },
      first_payment_pending: { icon: AlertCircle, color: 'bg-yellow-100 text-yellow-700', label: 'Payment Pending' },
      qualified: { icon: CheckCircle, color: 'bg-green-100 text-green-700', label: 'Qualified' },
      disqualified: { icon: AlertCircle, color: 'bg-red-100 text-red-600', label: 'Disqualified' },
    };
    const config = styles[status] || styles.invited;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!passwordForm.currentPassword) {
      toast.error('Current password is required');
      return;
    }
    if (!passwordForm.newPassword) {
      toast.error('New password is required');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    try {
      await authAPI.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to change password';
      toast.error(errorMsg);
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl">
        {/* Header */}
        <div className="flex items-center gap-3">
          <User className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold font-heading">{t('profile.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('profile.subtitle')}</p>
          </div>
        </div>

        {/* Profile Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Account Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Name */}
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Name</p>
                <p className="font-medium">{user?.name}</p>
              </div>
            </div>
            
            {/* Email */}
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{user?.email}</p>
              </div>
            </div>
            
            {/* Role */}
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Role</p>
                <div className="mt-1">{getRoleBadge(user?.role)}</div>
              </div>
            </div>
            
            {/* Assigned Vehicle */}
            {(user?.assigned_van_id || assignedVehicle) && (
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Assigned Vehicle</p>
                  <p className="font-medium">
                    {assignedVehicle?.name || 'Loading...'}
                    {assignedVehicle?.van_number && (
                      <span className="text-muted-foreground ml-2">
                        #{assignedVehicle.van_number}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preferences — Language */}
        <Card data-testid="preferences-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="w-5 h-5 text-muted-foreground" />
              {t('profile.preferences')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-sm font-medium">{t('profile.language')}</Label>
                <p className="text-xs text-muted-foreground mt-1 max-w-md">
                  {t('profile.languageHint')}
                </p>
              </div>
              <LanguageSwitcher variant="inline" testIdPrefix="lang-profile" />
            </div>
          </CardContent>
        </Card>

        {/* Security Section - MFA */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Security</h2>
          </div>
          
          {/* Password Change Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <KeyRound className="w-4 h-4" />
                Change Password
              </CardTitle>
              <CardDescription>
                Update your password to keep your account secure
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                      placeholder="Enter current password"
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      placeholder="Enter new password (min. 6 characters)"
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <EyeOff className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    placeholder="Confirm new password"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  disabled={changingPassword || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                  className="w-full sm:w-auto"
                >
                  {changingPassword && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Change Password
                </Button>
              </form>
            </CardContent>
          </Card>
          
          {/* MFA Section */}
          <MFASecurity />
        </div>

        {/* Referral Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Gift className="w-5 h-5 text-primary" />
              Your Referral Code
            </CardTitle>
            <CardDescription>
              Share your code with others. When they subscribe, you earn a free month!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Referral Code */}
            <div className="flex items-center gap-2">
              <Input
                value={referralStats?.referral_code || ''}
                readOnly
                className="font-mono text-lg max-w-[200px] font-bold"
              />
              <Button variant="outline" size="icon" onClick={copyReferralCode}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-3 gap-4 pt-2">
              <div className="text-center p-3 bg-muted rounded-lg">
                <p className="text-2xl font-bold">{referralStats?.total_referrals || 0}</p>
                <p className="text-xs text-muted-foreground">Referrals</p>
              </div>
              <div className="text-center p-3 bg-muted rounded-lg">
                <p className="text-2xl font-bold">{referralStats?.qualified_referrals || 0}</p>
                <p className="text-xs text-muted-foreground">Qualified</p>
              </div>
              <div className="text-center p-3 bg-green-100 rounded-lg">
                <p className="text-2xl font-bold text-green-700">
                  {referralStats?.free_months_earned || 0}
                </p>
                <p className="text-xs text-green-600">Free Months</p>
              </div>
            </div>

            {/* Referral List */}
            {referralStats?.referrals?.length > 0 && (
              <div className="pt-4 border-t">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Your Referrals
                </h4>
                <div className="space-y-2">
                  {referralStats.referrals.map((ref) => (
                    <div 
                      key={ref.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium">
                          {ref.referred_tenant_name || ref.referred_email || 'Pending signup'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(ref.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      {getStatusBadge(ref.status)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No referrals message */}
            {(!referralStats?.referrals || referralStats.referrals.length === 0) && (
              <div className="text-center py-4 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No referrals yet. Share your code to get started!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Legal Section */}
        <Card data-testid="profile-legal-section">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Legal
            </CardTitle>
            <CardDescription>Terms of service and privacy policy</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <a
              href="https://stockandload.com/terms-of-service/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-lg border p-3 hover:bg-muted/50 transition-colors"
              data-testid="profile-terms-link"
            >
              <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="text-sm font-medium">Terms of Service</span>
            </a>
            <a
              href="https://stockandload.com/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-lg border p-3 hover:bg-muted/50 transition-colors"
              data-testid="profile-privacy-link"
            >
              <Shield className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="text-sm font-medium">Privacy Policy</span>
            </a>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
